const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;

    // 1. Local Machine Mode (localhost / 127.0.0.1)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (envUrl) {
        const clean = envUrl.replace(/\/+$/, '');
        return clean.endsWith('/api') ? clean : `${clean}/api`;
      }
      return `${protocol}//${hostname}:5000/api`;
    }

    // 2. Admin Dashboard Auto-Detection (e.g. demodashboard.freshmindz.in -> demoapi.freshmindz.in)
    if (hostname.includes('demo') && !hostname.includes('demoapi')) {
      const apiHost = hostname.replace(/demo(dashboard)?/, 'demoapi');
      return `${protocol}//${apiHost}/api`;
    }
    if (hostname.includes('dashboard') || hostname.includes('admin')) {
      const apiHost = hostname.replace(/(dashboard|admin)/, 'api');
      return `${protocol}//${apiHost}/api`;
    }

    // 3. Environment Variable if defined and not localhost
    if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
      const clean = envUrl.replace(/\/+$/, '');
      return clean.endsWith('/api') ? clean : `${clean}/api`;
    }

    // 4. Default dynamic relative path '/api'
    return '/api';
  }

  if (envUrl) {
    const clean = envUrl.replace(/\/+$/, '');
    return clean.endsWith('/api') ? clean : `${clean}/api`;
  }
  return '/api';
};

export const API_BASE_URL = getApiBaseUrl();

export const SERVER_BASE_URL = (process.env.REACT_APP_SERVER_URL || (API_BASE_URL.startsWith('http') ? API_BASE_URL.replace(/\/api\/?$/, '') : '')).replace(/\/+$/, '');

export const resolveImageUrl = (img) => {
  if (!img) return '';
  if (typeof img !== 'string') return img;
  if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) {
    return img;
  }
  const cleanPath = img.startsWith('/') ? img : `/${img}`;
  if (SERVER_BASE_URL) {
    return `${SERVER_BASE_URL}${cleanPath}`;
  }
  return cleanPath;
};

// Helper for fetch API requests with fallback
async function fetchApi(endpoint, options = {}, fallbackData = null) {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const data = await res.json();
    if (endpoint === '/settings') {
      return { settings: data.data || {}, achievements: data.achievements || {} };
    }
    return data.data !== undefined ? data.data : data;
  } catch (err) {
    console.warn(`[API] Endpoint ${endpoint} unreachable:`, err.message);
    return fallbackData;
  }
}

// 1. Fetch API Helpers
export const getSliders = async (fallback) => fetchApi('/sliders', {}, fallback);
export const getProducts = async (params = {}, fallback = []) => fetchApi('/products', {}, fallback);
export const getProjects = async (params = {}, fallback = []) => fetchApi('/projects', {}, fallback);
export const getTestimonials = async (fallback = []) => fetchApi('/testimonials?admin=true', {}, fallback);
export const getSettings = async (fallback = {}) => fetchApi('/settings', {}, fallback);
export const getBanners = async (fallback = {}) => fetchApi('/page-banners', {}, fallback);

// ADMIN AUTH & CRUD
export const adminLogin = async (username, password) => {
  return authFetch('/auth/login', 'POST', { username, password });
};

async function authFetch(endpoint, method = 'GET', body = null, token) {
  try {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const options = { method, headers };
    if (body) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    const res = await fetch(`${API_BASE_URL}${endpoint}`, options);
    if (!res.ok) {
      try {
        const errJson = await res.json();
        if (errJson && errJson.message) {
          return { success: false, message: errJson.message };
        }
      } catch (e) {}
      return { success: false, message: res.status === 401 ? 'Invalid username or password.' : 'Server connection error.' };
    }
    return await res.json();
  } catch (err) {
    console.warn(`[Admin API Error] ${method} ${endpoint}:`, err.message);
    return { success: false, message: 'Backend server is offline or unreachable. Please start backend server (port 5000).' };
  }
}

export const adminGetInquiries = async (token) => authFetch('/inquiries', 'GET', null, token);
export const adminDeleteInquiry = async (id, token) => authFetch(`/inquiries/${id}`, 'DELETE', null, token);

export const adminCreateProduct = async (data, token) => authFetch('/products', 'POST', data, token);
export const adminUpdateProduct = async (id, data, token) => authFetch(`/products/${id}`, 'PUT', data, token);
export const adminDeleteProduct = async (id, token) => authFetch(`/products/${id}`, 'DELETE', null, token);

export const adminCreateSlider = async (data, token) => authFetch('/sliders', 'POST', data, token);
export const adminUpdateSlider = async (id, data, token) => authFetch(`/sliders/${id}`, 'PUT', data, token);
export const adminDeleteSlider = async (id, token) => authFetch(`/sliders/${id}`, 'DELETE', null, token);

export const adminCreateProject = async (data, token) => authFetch('/projects', 'POST', data, token);
export const adminUpdateProject = async (id, data, token) => authFetch(`/projects/${id}`, 'PUT', data, token);
export const adminDeleteProject = async (id, token) => authFetch(`/projects/${id}`, 'DELETE', null, token);

export const adminCreateTestimonial = async (data, token) => authFetch('/testimonials', 'POST', data, token);
export const adminUpdateTestimonial = async (id, data, token) => authFetch(`/testimonials/${id}`, 'PUT', data, token);
export const adminApproveTestimonial = async (id, token) => authFetch(`/testimonials/${id}/approve`, 'PUT', null, token);
export const adminDeleteTestimonial = async (id, token) => authFetch(`/testimonials/${id}`, 'DELETE', null, token);

export const adminUpdateSettings = async (data, token) => authFetch('/settings', 'PUT', data, token);
export const adminUpdateBanner = async (key, data, token) => authFetch(`/page-banners/${key}`, 'PUT', data, token);
export const adminUpdateCredentials = async (data, token) => authFetch('/auth/update-credentials', 'PUT', data, token);

export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      body: formData
    });
    return await res.json();
  } catch (err) {
    return { success: false, message: 'Image upload failed.' };
  }
};
